import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Package, ShoppingCart, Sparkles, Wallet } from 'lucide-react';
import { productsAPI, ordersAPI, walletAPI } from '../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

type AgentProduct = {
    id: string;
    title: string;
    description: string;
    price: number;               // قیمت دائمی (پایه)
    price_annual: string | null; // قیمت سالانه (اختیاری)
    agent_commission_pct: string; // درصد مجاز فروش مثل "30.00"
};

const fa = new Intl.NumberFormat('fa-IR');

const Products: React.FC = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<AgentProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [orderLoading, setOrderLoading] = useState<string | null>(null);
    const [qty, setQty] = useState<Record<string, number>>({});
    const [wallet, setWallet] = useState<{ balance?: number; available?: number } | null>(null);

    // موجودی
    const walletBalance = wallet?.balance ?? wallet?.available ?? 0;

    const changeQty = (productId: string, delta: number) => {
        setQty((prev) => {
            const current = Number(prev[productId] ?? 1);
            const next = Math.max(1, current + delta);
            return { ...prev, [productId]: next };
        });
    };

    useEffect(() => {
        (async () => {
            try {
                const [prods, wal] = await Promise.all([
                    productsAPI.getAgentProducts(),
                    walletAPI.getWalletInfo().catch(() => ({ balance: 0 })),
                ]);
                setProducts(prods);
                setWallet(wal);
                // مقدار پیش‌فرض تعداد = 1
                const initQty: Record<string, number> = {};
                prods.forEach((p: AgentProduct) => (initQty[p.id] = 1));
                setQty(initQty);
            } catch (err: any) {
                const msg =
                    err?.response?.data?.message ||
                    err?.message ||
                    'خطا در دریافت اطلاعات';
                setError(msg);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleCreateOrder = async (productId: string) => {
        const count = Math.max(1, qty[productId] || 1);

        setOrderLoading(productId);
        try {
            const result = await ordersAPI.createOrder({ productId: Number(productId), quantity: count });
            toast.success(`سفارش #${result.order.id} با موفقیت ثبت شد. برای پرداخت به صفحه سفارشات بروید.`);
            // هدایت به صفحه سفارشات برای پرداخت
            navigate('/orders');
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'خطا در ثبت سفارش';
            toast.error(msg);
        } finally {
            setOrderLoading(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
                    <p className="text-green-700 font-medium">در حال بارگذاری محصولات...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4" dir="rtl">
                <Alert variant="destructive" className="max-w-xl w-full">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    const totalProducts = products.length;

    return (
        <div
            dir="rtl"
            className="space-y-6 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 min-h-screen p-4 md:p-8"
        >
            {/* هدر شیشه‌ای + موجودی (هماهنگ با لایسنس‌ها) */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 p-6 md:p-8 shadow-2xl">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>

                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-3xl md:text-4xl font-bold">محصولات قابل فروش</h1>
                        </div>
                        <p className="text-green-100 text-lg">
                            مشاهده قیمت‌ها، کمیسیون‌ها و ثبت سفارش مستقیم از کیف‌پول نماینده
                        </p>

                        <div className="mt-3 flex flex-wrap gap-3">
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-sm border border-white/30">
                                <span className="opacity-80">تعداد محصولات:</span>{' '}
                                <span className="font-bold">{fa.format(totalProducts)}</span>
                            </div>
                        </div>
                    </div>

                    {/* کارت کیف‌پول */}
                    <div className="bg-white/15 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/30 min-w-[260px] max-w-sm">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Wallet className="h-6 w-6 text-emerald-100" />
                                <span className="text-sm text-emerald-50">موجودی کیف‌پول</span>
                            </div>
                        </div>
                        <div className="text-2xl md:text-3xl font-bold text-white">
                            {fa.format(walletBalance / 10)} <span className="text-emerald-100 text-base font-normal">تومان</span>
                        </div>
                        <p className="mt-1 text-xs text-emerald-100">
                            پس از ثبت سفارش، از صفحه سفارشات یا فاکتور می‌توانید پرداخت را انجام دهید.
                        </p>
                        <Button
                            variant="outline"
                            className="mt-3 w-full border-emerald-200 text-emerald-700 hover:bg-emerald-600/40 hover:text-white"
                            onClick={() => navigate('/wallet')}
                        >
                            مدیریت کیف‌پول
                        </Button>
                    </div>
                </div>
            </div>

            {/* اگر محصولی نیست */}
            {products.length === 0 ? (
                <Card className="border-green-200/60 bg-white/80 backdrop-blur-sm shadow-lg">
                    <CardContent className="text-center py-12">
                        <Package className="h-12 w-12 mx-auto mb-4 text-green-400" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">محصولی یافت نشد</h3>
                        <p className="text-gray-500">
                            در حال حاضر محصولی برای نمایش وجود ندارد
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((product) => {
                        const count = Math.max(1, qty[product.id] ?? 1);
                        const pct = parseFloat(product.agent_commission_pct || '0');

                        // محاسبات
                        const unitPrice = product.price;                                // قیمت هر واحد (پایه)
                        const totalSale = unitPrice * count;                             // مبلغ فروش کل (پایه × تعداد)
                        const commissionPerUnit = Math.round((unitPrice * pct) / 100);   // کمیسیون هر واحد
                        const totalCommission = commissionPerUnit * count;               // سود کل
                        const totalFinal = totalSale + totalCommission;                  // مبلغ نهایی فروش برای مشتری
                        const annualPrice = product.price_annual ? Number(product.price_annual) : null;

                        return (
                            <Card
                                key={product.id}
                                className="border-emerald-600/60 border-2 bg-white/85 backdrop-blur-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-200"
                            >
                                <CardHeader className="text-right pb-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <CardTitle className="text-lg text-green-900">
                                                {product.title}
                                            </CardTitle>
                                            <CardDescription className="text-sm text-green-700 mt-1 line-clamp-2">
                                                {product.description}
                                            </CardDescription>
                                        </div>
                                        <Badge variant="outline" className="border-green-300 text-green-700 text-xs">
                                            شناسه: {product.id}
                                        </Badge>
                                    </div>
                                </CardHeader>

                                <CardContent className="space-y-4">
                                    {/* قیمت‌ها */}
                                    <div className="rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 p-3 space-y-2 border border-green-100">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700 text-sm">قیمت محصول:</span>
                                            <span className="text-2xl font-bold text-green-700">
                                                {fa.format(unitPrice / 10)}{' '}
                                                <span className="text-sm font-normal">تومان</span>
                                            </span>
                                        </div>
                                        {annualPrice !== null && (
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-700">اشتراک سالانه:</span>
                                                <span className="font-semibold text-green-800">
                                                    {fa.format(annualPrice / 10)} تومان
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* درصد مجاز فروش + کمیسیون واحد */}
                                    <div className="rounded-xl border border-green-200 bg-green-50 p-3 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-800 text-sm">
                                                درصد مجاز فروش مازاد:
                                            </span>
                                            <Badge className="bg-green-600 text-white text-xs px-3 py-1">
                                                {pct}%
                                            </Badge>
                                        </div>
                                        <div className="flex items-center justify-between text-sm text-gray-700">
                                            <span>کمیسیون هر واحد:</span>
                                            <span className="font-semibold text-emerald-700">
                                                {fa.format(commissionPerUnit / 10)} تومان
                                            </span>
                                        </div>
                                    </div>

                                    {/* ⭐⭐⭐ تعداد — استپر کامل + / - ⭐⭐⭐ */}
                                    <div className="space-y-1 flex flex-row justify-between items-center">
                                        <label className="text-sm text-gray-700">
                                            تعداد لایسنس‌ها
                                        </label>

                                        <div className="inline-flex items-center rounded-lg border border-emerald-300 bg-white overflow-hidden">
                                            {/* دکمه کم */}
                                            <button
                                                type="button"
                                                onClick={() => changeQty(product.id, -1)}
                                                disabled={count <= 1 || orderLoading === product.id}
                                                className="h-10 w-10 flex items-center justify-center text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                                            >
                                                −
                                            </button>

                                            {/* ورودی */}
                                            <input
                                                type="number"
                                                min={1}
                                                value={count}
                                                onChange={e =>
                                                    setQty(prev => ({
                                                        ...prev,
                                                        [product.id]: Math.max(
                                                            1,
                                                            Number(e.target.value || 1)
                                                        )
                                                    }))
                                                }
                                                className="h-10 w-16 text-center border-x border-emerald-200 bg-white text-sm focus:outline-none"
                                            />

                                            {/* دکمه زیاد */}
                                            <button
                                                type="button"
                                                onClick={() => changeQty(product.id, +1)}
                                                disabled={orderLoading === product.id}
                                                className="h-10 w-10 flex items-center justify-center text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    {/* خلاصه مالی */}
                                    <div className="rounded-xl bg-white border border-green-200 p-3 space-y-2 text-sm">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">مبلغ پایه:</span>
                                            <span className="font-semibold text-gray-800">
                                                {fa.format(totalSale / 10)} تومان
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">سود نماینده:</span>
                                            <span className="font-semibold text-emerald-600">
                                                {fa.format(totalCommission / 10)} تومان
                                            </span>
                                        </div>

                                        <div className="border-t border-green-100 pt-2 flex items-center justify-between">
                                            <span className="text-gray-900 font-medium flex flex-col">
                                                <span> 💰 مبلغ نهایی فروش :</span>
                                                <span className={'text-[10px] text-slate-500'}>(قیمت پیشنهادی به مشتری)</span>
                                            </span>
                                            <span className="text-green-700 font-bold text-base md:text-sm">
                                                {fa.format(totalFinal / 10)} تومان
                                            </span>
                                        </div>
                                    </div>

                                    {/* ایجاد سفارش - بدون چک موجودی */}
                                    <Button
                                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                                        onClick={() => handleCreateOrder(product.id)}
                                        disabled={orderLoading === product.id}
                                    >
                                        {orderLoading === product.id ? (
                                            <>
                                                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                                در حال ایجاد سفارش…
                                            </>
                                        ) : (
                                            <>
                                                <ShoppingCart className="ml-2 h-4 w-4" />
                                                ایجاد سفارش
                                            </>
                                        )}
                                    </Button>

                                    <div className="text-[10px] text-emerald-600 text-center">
                                        پس از ثبت سفارش، برای پرداخت به صفحه سفارشات یا فاکتور بروید.
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Products;
